/**
 * mcpbDrag — resolve a dnd-kit drag-end into the slot mutation it implies.
 *
 * MCPlayback puts ALL drag-and-drop on dnd-kit (David 2026-06-23): cell↔cell swap, sidebar-pill→cell,
 * and tree-cam→cell share ONE DndContext. The cells are the drop targets (their droppable id IS the slot
 * index). Each drag SOURCE carries `data.current`:
 *   - { type: 'cell' | 'pill', slot }  — an already-LOADED slot (a grid cell, or its sidebar pill).
 *   - { type: 'tree', cam }            — a NEW camera dragged from the desktop tree.
 *
 * Keeping the routing pure lets the View's onDragEnd stay a thin dispatcher and lets us unit-test the
 * decision (dnd-kit pointer drags can't be reproduced in jsdom — the gesture itself is browser-verified).
 *
 * Outcomes:
 *   - cell/pill → cell : SWAP the two slots. A swap is what makes "drop on populated = replace, drop on
 *                        empty = fill" true at once (the target shows the dragged cam; the source takes
 *                        the target's) WITHOUT ever duplicating a camera. Dropping on its own slot = no-op.
 *   - tree → cell      : PLACE the new cam into that slot (fill an empty cell or replace an occupied one).
 *
 * @param {{type?: string, slot?: number, cam?: object}|null} activeData  dnd-kit active.data.current
 * @param {number|string|null} overId                                     dnd-kit over.id (the cell slot)
 * @returns {{kind:'swap', from:number, to:number} | {kind:'place', slot:number, cam:object} | null}
 */
export function resolveDrop(activeData, overId) {
  if (overId == null || !activeData) return null;
  const to = typeof overId === 'number' ? overId : Number(overId);
  if (Number.isNaN(to)) return null;

  const { type } = activeData;
  if (type === 'cell' || type === 'pill') {
    const from = activeData.slot;
    if (from == null || from === to) return null; // dropped on its own slot — nothing to do
    return { kind: 'swap', from, to };
  }
  if (type === 'tree') {
    if (!activeData.cam) return null;
    return { kind: 'place', slot: to, cam: activeData.cam };
  }
  return null;
}
